import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { api } from '@/services/api';
import React, { useState } from 'react';
import { Bell, Search, Menu, X, RefreshCw, Settings, User, LogOut, Sun, Moon } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { useThemeStore } from '@/store/themeStore';
import clsx from 'clsx';

interface HeaderProps {
  onToggleSidebar: () => void;
  sidebarOpen: boolean;
}

const Header: React.FC<HeaderProps> = ({ onToggleSidebar, sidebarOpen }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchOpen, setSearchOpen] = useState(false);
  const { data: search, isFetching: searching, refetch: searchRecords } = useQuery(['header-search',searchQuery], () => api.get<any>('/admin/search',{q:searchQuery}), {enabled:false});
  const { data: notificationResponse } = useQuery('header-notifications', () => api.get<any>('/notifications',{limit:6}),{refetchInterval:60000});
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const { user, logout } = useAuthStore();
  const { theme, toggleTheme } = useThemeStore();

  const notifications = (notificationResponse?.data?.notifications || []).map((n:any) => ({ id:n._id,title:n.title,message:n.message,time:new Date(n.createdAt).toLocaleString(),type:n.type,unread:!n.read }));

  const unreadCount = notifications.filter((n:any) => n.unread).length;
  const handleLogout = () => logout();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim().length >= 2) { setSearchOpen(true); searchRecords(); }
  };

  return (
    <header className="bg-white border-b border-slate-200 px-3 sm:px-4 py-2.5 sm:py-3 sticky top-0 z-40">
      {searchOpen && <div role="dialog" aria-label="Search results" className="absolute top-full left-4 right-4 sm:right-auto sm:w-96 bg-white border border-slate-200 rounded-xl shadow-xl p-4 z-50"><button onClick={()=>setSearchOpen(false)} className="float-right p-2" aria-label="Close search">×</button><h2 className="font-semibold mb-3">Search results</h2>{searching ? <p>Searching…</p> : <>{(search?.data?.users || []).map((u:any)=><button key={u._id} className="block w-full text-left p-2 hover:bg-slate-50" onClick={()=>{setSearchOpen(false);navigate(`/users/${u._id}`);}}>{u.username} · {u.email}</button>)}{(search?.data?.transactions || []).map((t:any)=><button key={t._id} className="block w-full text-left p-2 hover:bg-slate-50 break-all" onClick={()=>{setSearchOpen(false);navigate(`/transactions/${t._id}`);}}>{t.transactionId}</button>)}{!search?.data?.users?.length && !search?.data?.transactions?.length && <p className="text-slate-500">No matching records.</p>}</>}</div>}
      <div className="flex items-center justify-between">
        {/* Left */}
        <div className="flex items-center space-x-2 sm:space-x-4 flex-1 min-w-0">
          <button onClick={onToggleSidebar} className="lg:hidden p-2.5 rounded-lg hover:bg-slate-100 active:bg-slate-200 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center" aria-label="Toggle menu">
            {sidebarOpen ? <X className="w-5 h-5 text-slate-600" /> : <Menu className="w-5 h-5 text-slate-600" />}
          </button>

          <form onSubmit={handleSearch} className="hidden md:block flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input type="text" aria-label="Search users and transactions" placeholder="Search users, transactions..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 w-full bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
            </div>
          </form>
        </div>

        {/* Right */}
        <div className="flex items-center space-x-1 sm:space-x-2">
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="p-2.5 rounded-lg hover:bg-slate-100 active:bg-slate-200 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
            title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {theme === 'dark' ? (
              <Sun className="w-5 h-5 text-slate-600" />
            ) : (
              <Moon className="w-5 h-5 text-slate-600" />
            )}
          </button>

          {/* Refresh */}
          <button aria-label="Refresh data" onClick={() => queryClient.invalidateQueries()} className="p-2.5 rounded-lg hover:bg-slate-100 active:bg-slate-200 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center">
            <RefreshCw className="w-5 h-5 text-slate-600" />
          </button>

          {/* Notifications */}
          <div className="relative">
            <button aria-label="Open notifications" aria-expanded={showNotifications} onClick={() => { setShowNotifications(!showNotifications); setShowProfile(false); }}
              className="relative p-2.5 rounded-lg hover:bg-slate-100 active:bg-slate-200 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center">
              <Bell className="w-5 h-5 text-slate-600" />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">{unreadCount}</span>
              )}
            </button>

            {showNotifications && (
              <div className="fixed inset-x-0 bottom-0 sm:absolute sm:inset-auto sm:right-0 sm:top-full sm:mt-2 w-full sm:w-80 bg-white rounded-t-2xl sm:rounded-xl shadow-lg border border-slate-200 py-2 z-50 max-h-[70vh] sm:max-h-96 flex flex-col animate-slide-in-up sm:animate-fade-in">
                <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-900">Notifications</h3>
                  <button onClick={() => setShowNotifications(false)} className="sm:hidden p-1 rounded-full hover:bg-slate-100">
                    <X className="w-5 h-5 text-slate-400" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto overscroll-contain">
                  {notifications.map((n:any) => (
                    <div key={n.id} role="button" tabIndex={0} onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); event.currentTarget.click(); } }} onClick={async()=>{ await api.post(`/notifications/${n.id}/read`).catch(() => null); queryClient.invalidateQueries('header-notifications'); }} className={clsx('px-4 py-3 hover:bg-slate-50 active:bg-slate-100 border-l-4 cursor-pointer',
                      n.type === 'warning' && 'border-yellow-400', n.type === 'info' && 'border-blue-400', n.type === 'error' && 'border-red-400', n.unread && 'bg-slate-50')}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900">{n.title}</p>
                          <p className="text-sm text-slate-600 mt-1">{n.message}</p>
                          <p className="text-xs text-slate-400 mt-1">{n.time}</p>
                        </div>
                        {n.unread && <div className="w-2 h-2 bg-blue-500 rounded-full ml-2 mt-1 flex-shrink-0"></div>}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="px-4 py-3 border-t border-slate-200">
                  <button onClick={()=>{setShowNotifications(false);navigate('/promotions/notifications');}} className="text-sm text-purple-600 hover:text-purple-700 font-medium w-full text-center py-1">View all notifications</button>
                </div>
              </div>
            )}
          </div>

          {/* Profile */}
          <div className="relative">
            <button aria-label="Open account menu" aria-expanded={showProfile} onClick={() => { setShowProfile(!showProfile); setShowNotifications(false); }}
              className="flex items-center space-x-2 p-2 rounded-lg hover:bg-slate-100 active:bg-slate-200 transition-colors min-h-[44px]">
              <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white text-sm font-medium">{user?.username?.charAt(0).toUpperCase() || 'A'}</span>
              </div>
            </button>

            {showProfile && (
              <div className="fixed inset-x-0 bottom-0 sm:absolute sm:inset-auto sm:right-0 sm:top-full sm:mt-2 w-full sm:w-48 bg-white rounded-t-2xl sm:rounded-xl shadow-lg border border-slate-200 py-2 z-50 animate-slide-in-up sm:animate-fade-in">
                <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{user?.username}</p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                  </div>
                  <button onClick={() => setShowProfile(false)} className="sm:hidden p-1 rounded-full hover:bg-slate-100">
                    <X className="w-5 h-5 text-slate-400" />
                  </button>
                </div>

                <div className="py-1">
                  <button onClick={()=>{setShowProfile(false);navigate('/settings');}} className="flex items-center space-x-3 w-full px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 active:bg-slate-100 min-h-[44px]">
                    <User className="w-4 h-4" /><span>Profile</span>
                  </button>
                  <button onClick={()=>{setShowProfile(false);navigate('/settings');}} className="flex items-center space-x-3 w-full px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 active:bg-slate-100 min-h-[44px]">
                    <Settings className="w-4 h-4" /><span>Settings</span>
                  </button>
                </div>

                <div className="border-t border-slate-200 pt-1">
                  <button onClick={handleLogout} className="flex items-center space-x-3 w-full px-4 py-3 text-sm text-red-600 hover:bg-red-50 active:bg-red-100 min-h-[44px]">
                    <LogOut className="w-4 h-4" /><span>Sign out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {(showNotifications || showProfile) && (
        <div className="fixed inset-0 z-30" onClick={() => { setShowNotifications(false); setShowProfile(false); }} />
      )}
    </header>
  );
};

export default Header;